import { memo } from 'react';
import { TooltipPosition } from '@ds/elements/Tooltip/Tooltip.types';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import { IImmigrationDocumentGBIE, IImmigrationDocumentUS } from 'src/shared/types/Immigration';
import { getFormattedDate, UserDateFormat } from 'src/utils/formatters/formatDate';
import MoreStatusInformation from '../MoreStatusInformation/MoreStatusInformation';
import { getEadExpiration } from '../ImmigrationSnapshot.service';
import FieldWithTooltip from '../FieldWithTooltip/FieldWithTooltip';
import StatusImg1 from 'src/assets/images/immigration-status-img1.png';
import StatusImg2 from 'src/assets/images/immigration-status-img2.png';

/**
 * Component to display US immigration document details.
 */
const CountryUS = memo(
  ({
    status,
    expirationDate,
    petitionExpiration,
    visaClassification,
    visaExpiration,
    niveadExpiration,
    iveadExpiration,
    ivClassification,
    nivClassification,
    apExpiration,
    finalI94,
  }: IImmigrationDocumentUS) => {
    const { isMobile } = useScreenSize();
    const tooltipPosition = isMobile ? TooltipPosition.Bottom : TooltipPosition.Left;
    const rowColTwoClass = 'flex gap-1';

    return (
      <div>
        {/* Current Status Field */}
        <FieldWithTooltip
          label="Current Status"
          iconClass="status-info-icon"
          value={status}
          tooltipContent={
            <div className="flex flex-col gap-1.5">
              <p>Your current U.S. immigration status.</p>
              <img className="status-img" src={StatusImg1} />
              <img className="status-img" src={StatusImg2} />
            </div>
          }
          tooltipPosition={tooltipPosition}
        />

        {/* Status and Petition Expiration Fields */}
        {(expirationDate || petitionExpiration) && (
          <div className={rowColTwoClass}>
            <FieldWithTooltip
              label="Status Expiration"
              iconClass="status-expiration-icon"
              value={expirationDate}
              isDate={true}
              tooltipContent="The date your current immigration status in the United States expires."
              tooltipPosition={tooltipPosition}
            />
            <FieldWithTooltip
              label="Petition Expiration"
              iconClass="petition-icon"
              value={petitionExpiration}
              isDate={true}
              tooltipContent="Expiration date from the I-797 Approval Notice, or I-129S, or LCA, related to your Current Status."
              tooltipPosition={isMobile ? TooltipPosition.Left : tooltipPosition}
            />
          </div>
        )}

        {/* Entry Visa Field */}
        <FieldWithTooltip
          label="Entry Visa"
          iconClass="entry-visa-icon"
          value={
            visaClassification && visaExpiration
              ? `${visaClassification}, ${getFormattedDate(visaExpiration, UserDateFormat.get())}`
              : ''
          }
          tooltipContent="The date the most recently issued entry visa in your passport expires."
          tooltipPosition={tooltipPosition}
        />

        {/* EAD Card Field */}
        {(niveadExpiration || iveadExpiration) && (
          <FieldWithTooltip
            label={`${
              new Date(iveadExpiration).valueOf() >= new Date(niveadExpiration).valueOf()
                ? ivClassification || ''
                : nivClassification || ''
            } EAD Card`}
            iconClass="ead-icon"
            value={getEadExpiration(iveadExpiration, niveadExpiration)}
            isDate={true}
            tooltipContent="The date your Employment Authorization Document (EAD Card) expires."
            tooltipPosition={isMobile ? TooltipPosition.Right : tooltipPosition}
          />
        )}

        {/* Advance Parole and Final I-94 Fields */}
        {(apExpiration || finalI94) && (
          <div className={rowColTwoClass}>
            <FieldWithTooltip
              label="Advance Parole"
              iconClass="advance-parole-icon"
              value={apExpiration}
              isDate={true}
              tooltipContent="The date your Advance Parole Travel Document expires."
              tooltipPosition={tooltipPosition}
            />
            <FieldWithTooltip
              label="Final I-94 Date"
              iconClass="final-i94-date-icon"
              value={finalI94}
              isDate={true}
              tooltipContent="If you are in H-1B, L-1, R-1 or P status, this is the total amount of time you are permitted to hold this immigration status."
              tooltipPosition={isMobile ? TooltipPosition.Left : tooltipPosition}
            />
          </div>
        )}
      </div>
    );
  }
);

/**
 * Component to display UK, Ireland immigration document details.
 */
const CountryGBIE = memo(
  ({
    workPermitExpirationDate,
    residencePermitExpirationDate,
    entryPermitExpirationDate,
  }: IImmigrationDocumentGBIE) => {
    return (
      <div>
        <FieldWithTooltip
          label="Work Permit Expiration"
          iconClass="work-permit-expiration-icon"
          value={workPermitExpirationDate}
          isDate={true}
          tooltipContent="The expiration date of your last issued Work Permit document."
        />

        <FieldWithTooltip
          label="Residence Permit Expiration"
          iconClass="residence-permit-expiration-icon"
          value={residencePermitExpirationDate}
          isDate={true}
          tooltipContent="The expiration date of your last issued Residence Permit document."
        />

        <FieldWithTooltip
          label="Entry Permit Expiration"
          iconClass="entry-permit-expiration-icon"
          value={entryPermitExpirationDate}
          isDate={true}
          tooltipContent="Expiration date from the Entry Visa stamp in your passport."
        />
      </div>
    );
  }
);

interface IImmigrationCountryContent<T> {
  isUS: boolean;
  profileId: number;
  data: T;
}

const ImmigrationCountryContent = <T,>({ isUS, profileId, data }: IImmigrationCountryContent<T>) => {
  return (
    <>
      {isUS ? (
        <CountryUS {...(data as IImmigrationDocumentUS)} />
      ) : (
        <CountryGBIE {...(data as IImmigrationDocumentGBIE)} />
      )}
      <MoreStatusInformation id={profileId} />
    </>
  );
};

export default memo(ImmigrationCountryContent);
